import FollowPopup from '@/component/popups/ui-3/FollowPopup';
import { getLoyaltyPoint, postLoyaltyPointWithdraw } from '@/Redux/futures/loyalty-point/actions';
import { formatDataNumberToen } from '@/utils/helper';
import imgBtn from '@assets/web-v3-images/btn-login.png';
import frameBottom from '@assets/web-v3-images/Frame-bottom.png';
import frameCenter from '@assets/web-v3-images/Frame-center.png';
import frameTop from '@assets/web-v3-images/Frame-top.png';
import imgError from '@assets/web-v3-images/something-went-wrong.png';
import imgCheck from '@assets/web-v3-images/icons/check.png';
import FrameActive from '@assets/web-v3-images/icons/Frame1.png';
import Frame from '@assets/web-v3-images/icons/Frame2.png';
import { Text } from '@radix-ui/themes';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import Confirm from './popup/Confirm';
import RedeemFailed from './popup/RedeemFailed';

const REDEEM_TIMEOUT_MS = 30000;

export default function RedeemRewardsCard({
	options = [],
	remaining = 0,
	total = 0,
	onSelect,
	balance,
	today_withdrawn
}) {
	const { t } = useTranslation()
	const dispatch = useDispatch();
	const [selected, setSelected] = useState(null);
	const [data, setData] = useState(null);
	const [openSuccess, setOpenSuccess] = useState(false);
	const [openConfirm, setOpenConfirm] = useState(false);
	const [isDisabled, setIsDisabled] = useState(false);
	const [unlocksTime, setUnlocksTime] = useState(false);
	const [openError, setOpenError] = useState(false);
	const [disabledPopup, setDisabledPopup] = useState(false);
	const isRequestingRef = useRef(false);
	const timeoutRef = useRef(null);

	const selectableLp = useMemo(() => {
		if (!options?.length) return null;
		const numericOptions = options.map(Number);
		const maxOption = Math.max(...numericOptions);
		if (balance >= maxOption) return maxOption;
		const affordable = numericOptions.filter((lp) => lp <= balance);
		if (!affordable.length) return numericOptions[0];
		return Math.max(...affordable);
	}, [options, balance]);

	const handleRedeem = () => {
		if (today_withdrawn) {
			setUnlocksTime(true) // show đã rủ LP rồi
		} else {
			if (balance < selected) {
				setIsDisabled(true)
			} else {
				setOpenConfirm(true); // chưa rút LP
			}
		}
	};

	// hiện popup lỗi chung + trả LP về ví ngay lập tức (refetch balance)
	const showError = () => {
		clearTimeout(timeoutRef.current);
		isRequestingRef.current = false;
		setOpenConfirm(false);
		setDisabledPopup(false);
		setOpenError(true);
		dispatch(getLoyaltyPoint({})); // LP quay về ví ngay khi popup hiện
	};

	const getWithdraw = (res, isLoading, errRes) => {
		if (isLoading) {
			setOpenConfirm(true);
			setDisabledPopup(true);
			return;
		}
		// đã timeout trước đó (case quá 30s) -> bỏ qua response trễ, không xử lý lại
		if (!isRequestingRef.current) return;

		if (res?.success) {
			clearTimeout(timeoutRef.current);
			isRequestingRef.current = false;
			setData(res.data || errRes?.data);
			setOpenConfirm(false);
			setOpenSuccess(true);
			setDisabledPopup(false);
			dispatch(getLoyaltyPoint({}));
		} else {
			// case 1: Payment trả lỗi | case 2: mất mạng giữa chừng
			setData(errRes);
			showError();
		}
	};

	const handleClick = () => {
		// case 4: user bấm Redeem nhiều lần liên tiếp khi yêu cầu trước còn đang chạy
		if (isRequestingRef.current) {
			showError();
			return;
		}

		isRequestingRef.current = true;
		timeoutRef.current = setTimeout(() => {
			// case 3: quá 30 giây không phản hồi
			showError();
		}, REDEEM_TIMEOUT_MS);

		const body = {
			milestone_lp: selected,
			callback: getWithdraw,
		};
		dispatch(postLoyaltyPointWithdraw(body));
	}

	useEffect(() => {
		return () => clearTimeout(timeoutRef.current);
	}, []);

	useEffect(() => {
		setSelected(selectableLp);
	}, [selectableLp])

	return (
		<>
			<div className="relative w-full mb-5 mt-4">
				{/* Top cap - cố định, không lặp */}
				<img src={frameTop} alt="" className="w-full block select-none" draggable={false} />

				{/* Phần giữa - co giãn theo nội dung */}
				<div
					className="w-full flex-1 px-6 sm:px-10 py-2 -mt-1"
					style={{
						backgroundImage: `url(${frameCenter})`,
						backgroundRepeat: 'repeat-y',
						backgroundSize: '100% 100%',
					}}
				>
					<div className="absolute inset-x-0 top-5 text-center font-bold text-[27px] text-[rgba(113,59,18,1)]">
						{t("loyalty_point.redeem_rewards")}
					</div>

					<p className="text-center font-semibold text-[26px] text-[rgba(161,92,7,1))] mb-0.5">
						{t("loyalty_point.todays_redemption")}
					</p>
					<p className="text-center font-semibold text-[26px] text-[rgba(161,92,7,1))] mb-5">
						{t("loyalty_point.remaining")}: {remaining} {t("loyalty_point.of")} {total}
					</p>

					{/* Options */}
					<div className="flex flex-wrap justify-center gap-3 mb-5 pt-5">
						{options?.map((lp, index) => {
							const isActive = lp === selected;
							const isDisabled = false; // cho phép chọn options kể cả khi balance = 0
							return (
								<div
									key={`${lp}-${index}`}
									className="relative w-[calc(25%-9px)]"
								>
									<button
										type="button"
										onClick={() => {
											if (isDisabled) return;
											setSelected(lp);
											onSelect?.(lp);
											setIsDisabled(isDisabled)
										}}
										className={`relative w-full text-center transition-colors overflow-hidden rounded-2xl
											${isDisabled ? 'opacity-40 cursor-not-allowed' : ''
											}`}
									>
										<img
											src={isActive ? FrameActive : Frame}
											alt=""
											className="w-full h-auto block"
										/>

										<div className="absolute inset-0 flex items-center justify-center">
											<span
												className={`font-bold text-[32px] ${isActive
													? 'text-[rgba(224,79,22,1)]'
													: 'text-[rgba(119,41,23,1)]'
													}`}
											>
												{formatDataNumberToen(lp)} LP
											</span>
										</div>
									</button>

									{isActive && (
										<img
											src={imgCheck}
											alt=""
											className="absolute -top-1.5 -right-1.5 w-10 z-10"
										/>
									)}
								</div>
							);
						})}
					</div>

					{/* Redeem button */}
					<button
						disabled={isDisabled}
						className={`relative w-full btn-click-active max-w-[100%] mt-[1%] outline-none ${isDisabled ? 'opacity-40 cursor-not-allowed' : 'cursor-pointer'
							}`}
						onClick={handleRedeem}
					>
						<img
							src={imgBtn}
							className="block w-full h-full"
							alt="Revive"
						/>

						<Text
							className="absolute inset-0 flex items-center justify-center text-3xl font-extrabold text-white"
							style={{
								WebkitTextStroke: "1px #FF692E",
								paintOrder: "stroke fill",
							}}
						>
							{t("loyalty_point.redeem")}
						</Text>
					</button>
				</div>

				{/* Bottom cap - cố định, không lặp */}
				<img src={frameBottom} alt="" className="w-full block select-none -mt-1" draggable={false} />
			</div>

			{/* show xác nhận đổi thưởng */}
			<Confirm
				open={openConfirm}
				onOpenChange={setOpenConfirm}
				handleClick={handleClick}
				selected={selected}
				balance={balance}
				// true để click ra ngoài không đóng được khi api call xong mới cho tiếp tục hành động tiếp theo
				disableOutsideClose={true}
				//true để disabled button trên popup này khi api call xong mới cho tiếp tục hành động tiếp theo
				disabledPopup={disabledPopup}
			/>

			{/* show lỗi */}
			<RedeemFailed
				open={openError}
				onOpenChange={setOpenError}
				msg={t("loyalty_point.something_unexpected_happened_please_try_again")}
				title={t("loyalty_point.something_went_wrong")}
				images={imgError}
				textBtn={t("loyalty_point.retry")}
			/>

			{/* show khi đã đổi thưởng rồi */}
			<RedeemFailed
				open={unlocksTime}
				onOpenChange={setUnlocksTime}
				msg={t("loyalty_point.redeem_limit_reached")}
				title={t("loyalty_point.redeem_failed")}
				textBtn={t("loyalty_point.got_it")}
			/>

			{/* check show khi không đủ điền kiện để đổi LP */}
			<RedeemFailed
				open={isDisabled}
				onOpenChange={setIsDisabled}
				msg={t("loyalty_point.earn_more_Loyalty_Points_to_unlock_this_milestone")
					.replace("_LP_", balance + '/' + selected)}
				title={t("loyalty_point.redeem_failed")}
				textBtn={t("loyalty_point.got_it")}
			/>

			{/* show nhận giải thành công */}
			<FollowPopup
				open={openSuccess}
				onOpenChange={setOpenSuccess}
				type="3"
				textBtn={t('loyalty_point.got_it')}
				value={data}
				handleClick={() => {
					setOpenSuccess(false);
				}}
			/>
		</>
	);
}